import {
  Text,
  TextField,
  SelectField,
  TextLink,
  ContentBlock,
  Heading,
  SvgIcon,
  Primary,
} from '@cinch-labs/design-system'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import styles from './billing-address.module.css'
import {
  AutocompleteSuggestion,
  findAddresses,
  getAddressDetails,
} from '../../services/ideal-postcodes'
import { useMaintenanceContext } from '../../context/maintenance-context'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form'
import { isValidPostcode } from '../../utils/postcode-helper'
import { FormData } from '../contact-details/contact-details'

export enum PostcodeErrorState {
  None = 'none',
  InvalidPostcode = 'invalid-postcode',
  PostcodeNotFound = 'postcode-not-found',
  NetworkError = 'network-error',
  NoAddressConfirmed = 'no-address-confirmed',
}

export interface BillingAddressProps {
  register: UseFormRegister<FormData>
  errors: FieldErrors<FormData>
  hasSelectAddressError: boolean
  setHasSelectAddressError: Dispatch<SetStateAction<boolean>>
  overrideAddressError: boolean
  setOverrideAddressError: Dispatch<SetStateAction<boolean>>
  setValue: UseFormSetValue<FormData>
  clearErrors: UseFormClearErrors<FormData>
  reset: UseFormReset<FormData>
}

export const BillingAddress = ({
  register,
  errors,
  hasSelectAddressError,
  setHasSelectAddressError,
  overrideAddressError,
  setOverrideAddressError,
  setValue,
  clearErrors,
  reset,
}: BillingAddressProps) => {
  const [postcodeErrorState, setPostcodeErrorState] =
    useState<PostcodeErrorState>(PostcodeErrorState.None)
  const [addresses, setAddresses] = useState<AutocompleteSuggestion[]>()
  const [enterAddressManually, setEnterAddressManually] =
    useState<boolean>(false)
  const [enteredPostCode, setPostCode] = useState('')
  const [hasEnteredPostCode, setHasEnteredPostCode] = useState<boolean>(false)
  const [showLookupByPostcode, setShowLookupByPostcode] =
    useState<boolean>(false)
  const [selectedAddress, setSelectedAddress] = useState<string[]>([])
  const [addressId, setAddressId] = useState<string>()
  const { postcode } = useMaintenanceContext()

  useEffect(() => {
    if (overrideAddressError) {
      setPostcodeErrorState(PostcodeErrorState.NoAddressConfirmed)
    }
  }, [overrideAddressError])

  const handleChange = (value: string) => {
    setOverrideAddressError(false)
    setPostcodeErrorState(PostcodeErrorState.None)
    setHasSelectAddressError(false)
    setHasEnteredPostCode(true)
    setShowLookupByPostcode(true)
    setAddresses([])
    setPostCode(value)
  }
  const postCodeValue =
    postcode && !hasEnteredPostCode ? postcode : enteredPostCode

  const hasAnyPostcodeError = postcodeErrorState !== PostcodeErrorState.None

  const getPostcodeErrorMessage = (): string => {
    switch (postcodeErrorState) {
      case PostcodeErrorState.InvalidPostcode: {
        return 'Please enter a valid postcode (e.g. PR15JW).'
      }
      case PostcodeErrorState.PostcodeNotFound: {
        return 'No addresses found for this postcode.'
      }
      case PostcodeErrorState.NetworkError: {
        return 'Error looking up address. Please try again or enter manually.'
      }
      case PostcodeErrorState.NoAddressConfirmed: {
        return 'Please enter a valid postcode and find an address.'
      }
      case PostcodeErrorState.None: {
        throw new Error(
          'getPostcodeErrorMessage called when there is no postcode error',
        )
      }
    }
  }

  const submitPostcode = async () => {
    setShowLookupByPostcode(false)
    setPostcodeErrorState(PostcodeErrorState.None)

    if (!isValidPostcode(postCodeValue)) {
      setPostcodeErrorState(PostcodeErrorState.InvalidPostcode)
      return
    }
    try {
      const response = await findAddresses(postCodeValue)
      if (!response || response.length === 0) {
        setPostcodeErrorState(PostcodeErrorState.PostcodeNotFound)
        return
      }
      setAddresses(response)
    } catch {
      setPostcodeErrorState(PostcodeErrorState.NetworkError)
    }
  }

  const handleEditAddress = async () => {
    setEnterAddressManually(!enterAddressManually)

    if (addressId) {
      const address = await getAddressDetails(addressId)
      setValue('addressLine1', address?.addressLine1)
      setValue('addressLine2', address?.addressLine2)
      setValue('city', address?.postTown)
      setValue('county', address?.county)
    }
  }

  useEffect(() => {
    if (enterAddressManually) {
      clearErrors()
      reset({
        addressLine1: '',
        addressLine2: '',
        city: '',
        county: '',
        enteredPostcode: postCodeValue,
      })
    }
  }, [enterAddressManually, postCodeValue])

  const handleLookupByPostcodeLinkClick = () => {
    setAddresses([])
    setSelectedAddress([])
    setHasSelectAddressError(false)
    setEnterAddressManually(!enterAddressManually)
    setShowLookupByPostcode(true)
    clearErrors()
    setPostcodeErrorState(PostcodeErrorState.None)

    if (enterAddressManually) {
      setPostCode('')
      setHasEnteredPostCode(true)
      setValue('enteredPostcode', '')
    }
  }

  const handleSelectAddress = (address: string, id: string) => {
    setAddressId(id)
    const customerAddress = address.split(',')
    setSelectedAddress(customerAddress)
  }

  return (
    <div>
      <ContentBlock>
        <Heading element="h4" className={styles.heading}>
          Your billing address
        </Heading>
      </ContentBlock>

      {enterAddressManually ? (
        <div className={styles.form}>
          <TextField
            id="address_line_1"
            label="Address line 1"
            type="text"
            fullWidth
            aria-describedby={
              errors.addressLine1 ? 'address-line-1-error' : undefined
            }
            classNames={{
              container: errors.addressLine1
                ? styles.formTextFieldError
                : styles.formTextField,
            }}
            {...register('addressLine1', {
              required:
                'Please enter an address line 1 (name/number and street name).',
            })}
            error={{
              show: !!errors.addressLine1,
              message: errors.addressLine1?.message,
            }}
          />
          <TextField
            id="address_line_2"
            label="Address line 2"
            type="text"
            optional
            fullWidth
            classNames={{
              container: styles.formTextField,
            }}
            {...register('addressLine2')}
          />
          <TextField
            id="town_city"
            label="Town/city"
            type="text"
            classNames={{
              container: errors.city
                ? styles.formTextFieldError
                : styles.formTextField,
            }}
            fullWidth
            {...register('city', { required: 'Please enter a town/city.' })}
            error={{
              show: !!errors.city,
              message: errors.city?.message,
            }}
          />
          <TextField
            id="county"
            label="County"
            type="text"
            classNames={{
              container: errors.county
                ? styles.formTextFieldError
                : styles.formTextField,
            }}
            fullWidth
            {...register('county', { required: 'Please enter a county.' })}
            error={{
              show: !!errors.county,
              message: errors.county?.message,
            }}
          />

          <div className={styles.postcodeContainer}>
            <TextField
              id="manual_postcode_input"
              label="Postcode"
              type="text"
              aria-describedby={
                errors.enteredPostcode ? 'manual-postcode-error' : undefined
              }
              aria-invalid={!!errors.enteredPostcode}
              classNames={{
                container: errors.enteredPostcode
                  ? styles.formTextFieldError
                  : styles.postcodeInput,
              }}
              {...register('enteredPostcode', {
                required: 'Please enter a postcode.',
                onChange: () => setHasSelectAddressError(false),
                validate: (value) =>
                  isValidPostcode(value) ||
                  'Please enter a valid postcode (e.g. PR15JW).',
              })}
              error={{
                show: !!errors.enteredPostcode,
                message: errors.enteredPostcode?.message,
              }}
            />
            <div
              className={
                errors.enteredPostcode ? styles.textLinkError : styles.textLink
              }
            >
              <TextLink onClick={handleLookupByPostcodeLinkClick}>
                <Text fontSizeFixed="s" fontWeight="semibold">
                  <SvgIcon name="rotate" />
                  Change address
                </Text>
              </TextLink>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.form}>
          {!selectedAddress ||
            (selectedAddress.length === 0 && (
              <>
                <div className={styles.postcodeLookupContainer}>
                  <TextField
                    classNames={{ container: styles.postcodeInput }}
                    id="postcode_input"
                    label="Postcode"
                    value={postCodeValue}
                    type="text"
                    aria-describedby={
                      hasAnyPostcodeError ? 'postcode-error' : 'postcode input'
                    }
                    aria-invalid={hasAnyPostcodeError}
                    {...register('enteredPostcode', {
                      required: 'Please enter a valid postcode (e.g. PR15JW).',
                      onChange: (e) => handleChange(e.target.value),
                    })}
                    error={{
                      show: !!errors.enteredPostcode || hasAnyPostcodeError,
                      message:
                        errors.enteredPostcode?.message ||
                        (hasAnyPostcodeError ? getPostcodeErrorMessage() : ''),
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        submitPostcode()
                      }
                    }}
                  />
                  <Primary
                    type="button"
                    onClick={() => submitPostcode()}
                    classNames={{
                      button: hasAnyPostcodeError
                        ? styles.errorButton
                        : styles.formButton,
                    }}
                  >
                    Find
                  </Primary>
                </div>

                {addresses && addresses.length > 0 && !showLookupByPostcode && (
                  <SelectField
                    id={'selectedAddress'}
                    options={addresses.map((address) => ({
                      label: address.suggestion,
                      value: `${address.suggestion}ADDRESS_ID=${address.id}`,
                    }))}
                    classNames={{
                      container: styles.selectField,
                    }}
                    label="Choose address"
                    placeholder={`Choose an address (${addresses.length} found)`}
                    {...register('selectedAddress', {
                      required:
                        'Please select a billing address, or enter it manually.',
                      setValueAs: (val) => ({
                        suggestedAddress:
                          typeof val === 'string'
                            ? val.split('ADDRESS_ID=')[0]
                            : '',
                        addressId:
                          typeof val === 'string'
                            ? val.split('ADDRESS_ID=')[1]
                            : '',
                      }),
                      onChange: (e) =>
                        handleSelectAddress(
                          e.target.value.split('ADDRESS_ID=')[0],
                          e.target.value.split('ADDRESS_ID=')[1],
                        ),
                    })}
                    error={{
                      show: !!errors.selectedAddress,
                      message: errors.selectedAddress?.message,
                    }}
                  />
                )}
              </>
            ))}
          {selectedAddress && selectedAddress.length > 0 && (
            <div className={styles.selectedAddressContainer}>
              {selectedAddress.map((addressLine, index) => (
                <Text
                  key={index}
                  className={
                    index === selectedAddress.length - 1
                      ? styles.addressLastLine
                      : styles.addressLines
                  }
                >
                  {index === selectedAddress.length - 1
                    ? enteredPostCode
                      ? enteredPostCode.toLocaleUpperCase()
                      : postcode?.toLocaleUpperCase()
                    : addressLine}
                </Text>
              ))}
              <TextLink
                onClick={() => {
                  handleEditAddress()
                }}
              >
                <Text
                  fontSizeFixed="s"
                  fontWeight="semibold"
                  className={styles.editAddressLink}
                >
                  Edit address
                </Text>
              </TextLink>
            </div>
          )}
          {/* Show manual entry button when: 1) addresses found, or 2) valid postcode but no addresses/network error */}
          {(addresses &&
            addresses.length > 0 &&
            !showLookupByPostcode &&
            (!selectedAddress || selectedAddress.length === 0)) ||
          postcodeErrorState === PostcodeErrorState.PostcodeNotFound ||
          postcodeErrorState === PostcodeErrorState.NetworkError ? (
            <div
              className={
                errors.selectedAddress ? styles.textLinkError : styles.textLink
              }
            >
              <TextLink
                onClick={() => setEnterAddressManually(!enterAddressManually)}
              >
                <Text fontSizeFixed="s" fontWeight="semibold">
                  <SvgIcon name="edit-v2" />
                  Enter address manually
                </Text>
              </TextLink>
            </div>
          ) : null}
          {(!addresses || addresses.length === 0 || showLookupByPostcode) && (
            <div>
              {!hasSelectAddressError ? (
                <div></div>
              ) : (
                <div className={styles.selectError}>
                  <SvgIcon name="error" className={styles.svgIcon} />
                  <Text fontSize="s">Please select a billing address</Text>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
